import React, { FC, useEffect, useMemo } from 'react';

import { Box, Grid } from '@mui/material';
import { FormikValues, useFormik } from 'formik';

import {
  IRequestContractModel,
  pharmacistActions,
  pharmacistEvent,
  requestContract,
  WorkshiftStatus,
} from '@pharmaplan/common';
import {
  getDaysDistribution,
  mergeContractAttributes,
} from '@pharmaplan/common/helpers/utils';

import useViewEditContract from '../../../../hooks/workshift/useViewEditContract';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { resetDialog } from '../../../../reducers/dialogReducer';
import genericClasses from '../../../../theme/GenericClasses';
import { Constants } from '../../../../helpers/Constants';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import RequestContractDetails from './RequestContractDetails';
import RequestContractTimes from './RequestContractTimes';
import { Validator } from './validator';
import styles from './styles';
import usePreRequestLongTerm from '../../../../hooks/PreRequest/usePreRequestLongTerm';

interface IRequestContractModal {
  isAutoSyncRecord?: boolean;
}

const RequestContractModal: FC<IRequestContractModal> = ({
  isAutoSyncRecord,
}) => {
  const dispatch = useAppDispatch();

  const { intervalsInitial, getFormattedObj } = useViewEditContract();
  const { preRequestBooking } = usePreRequestLongTerm();

  const events = useAppSelector(pharmacistEvent);
  const { shifts, longTermShiftContract, counterShiftContract } = events ?? {};
  const isUpdate = useMemo(
    () =>
      shifts?.some(
        (item) =>
          item.status === WorkshiftStatus.Booked
          || item.status === WorkshiftStatus.Requested,
      ),
    [shifts],
  );

  const { allowanceNotes, weekDayPaidHourlyRate, weekendPaidHourlyRate } = longTermShiftContract ?? {};

  const {
    weekDayPaidHourlyRate: counterWeekDayPaidHourlyRate,
    weekendPaidHourlyRate: counterWeekendPaidHourlyRate,
    allowanceNotes: counterAllowanceNotes,
    shiftContractAttributes,
    counterShiftContractId,
  } = counterShiftContract ?? {};

  const counterShifts = useMemo(
    () =>
      shiftContractAttributes?.map((item) =>
        item.startDate?.split('T')[0])
      ?? [],
    [shiftContractAttributes],
  );

  const shiftsMerged = useMemo(
    () => {
      if (counterShiftContract && isUpdate) {
        return mergeContractAttributes(
          shifts ?? [],
          shiftContractAttributes?.map((item) =>
            ({
              ...item,
              changed: true,
            })) ?? [],
          { status: WorkshiftStatus.Requested },
        );
      }
      return shifts;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftContractAttributes, shifts],
  );

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSubmit = (values: FormikValues) => {
    const {
      isCounterOffer,
      workshiftIds,
      weekDayPaidHourlyRate: formikWeekdayPaidHourlyRate,
      weekendPaidHourlyRate: formikweekendPaidHourlyRate,
      proposedAllowance,
    } = values ?? {};

    const alreadySelectedShifts = shifts
      ?.filter((item) =>
        item.status === WorkshiftStatus.Requested)
      .map((item) =>
        item.workShiftId) ?? [];

    let params: IRequestContractModel = {
      workshiftIds: [...alreadySelectedShifts, ...workshiftIds],
    };

    if (isCounterOffer) {
      const formattedObj = Object.values(getFormattedObj(values));

      const changedItems = formattedObj.filter((item) =>
        item.changed);

      const onlySelectedShifts = shifts?.filter((item) =>
        workshiftIds.includes(item.workShiftId));

      const matchingShifts = changedItems.filter((cItem) =>
        onlySelectedShifts?.some(
          (sItem) =>
            sItem.startDate.split('T')[0] === cItem.startDate.split('T')[0],
        ));

      params = {
        isAutoSyncRecord,
        workshiftIds: [...alreadySelectedShifts, ...workshiftIds],
        counterOffer: {
          weekDayPaidHourlyRate: parseFloat(formikWeekdayPaidHourlyRate || 0),
          weekendPaidHourlyRate: parseFloat(formikweekendPaidHourlyRate || 0),
          allowanceNotes: proposedAllowance,
          shiftContractAttributes: matchingShifts,
        },
      };
    }

    preRequestBooking(workshiftIds?.[0] ?? '', () => {
      dispatch(requestContract(params));
    });
  };

  const { weekdays, weekends } = useMemo(
    () =>
      getDaysDistribution(shifts ?? []),
    [shifts],
  );

  const formik = useFormik({
    initialValues: {
      allowanceNotes,
      weekdays,
      weekends,
      isCounterOffer: !!counterShiftContractId,
      proposedAllowance: counterAllowanceNotes,
      workshiftIds: [],
      weekDayPaidHourlyRate:
        counterWeekDayPaidHourlyRate ?? weekDayPaidHourlyRate,
      weekendPaidHourlyRate:
        counterWeekendPaidHourlyRate ?? weekendPaidHourlyRate,
    },
    onSubmit: handleSubmit,
    validationSchema: Validator(),
  });

  useEffect(() => {
    if (shiftsMerged) {
      formik.setValues({
        ...formik.values,
        ...intervalsInitial(shiftsMerged),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftsMerged]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <Grid columnSpacing="15px" container>
          <Grid md={4} xs={12} item>
            <RequestContractDetails
              isUpdate={isUpdate}
              events={events}
              successAction={pharmacistActions.getPharmacistEvent}
              formik={formik}
            />
          </Grid>
          <Grid md={8} xs={12} item>
            <RequestContractTimes
              counterShifts={counterShifts}
              isUpdate={isUpdate}
              formik={formik}
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={[styles.buttonContainer, genericClasses.flex]}>
        <CustomButton
          label={strings.cancel}
          onClick={handleClose}
          variant={Constants.variant.outlined}
        />
        <CustomButton
          customClass={styles.saveButton}
          onClick={formik.handleSubmit}
          label={strings.requestBooking}
          disabled={!formik.values?.workshiftIds.length}
        />
      </Box>
    </Box>
  );
};

export default RequestContractModal;
